import { CSSProperties, forwardRef, useEffect, useImperativeHandle, useMemo } from "react";
import { DefaultButton, Stack, Text, mergeStyleSets } from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import {
  LoadingOverlay,
  MessageBar,
  css,
  logger,
  stringUtils,
  themeUtils,
  useTheme,
} from "cayo.ui";
import styled from "styled-components";
import { IButton, IFormField } from "@cayo/shared/api/types/schema";
import { setModelProperty } from "@cayo/shared/components/Form/modelUtils/setModelProperty";
import { getButtonThemeStyles } from "@cayo/shared/components/Form/renderers/renderers.buttons";
import { FormProps } from "@cayo/shared/components/Form/types";
import FormFooter from "./FormFooter";
import useForm from "./hooks/useForm";
import modelUtils from "./modelUtils";
import { ModelExpressionEvaluator } from "../../scheme/transforms/model-expression-evaluator";

const log = logger.getLogger("Form");
const defaultHorizontalPadding = "20px";

interface FormRef {
  getModel: () => Record<string, any>;
}

const Form = forwardRef<FormRef, FormProps>((props, ref) => {
  const formId: string = useId("formId");
  const theme = useTheme();
  const { cayoTheme } = theme;

  const {
    signals,
    className,
    title,
    footer,
    isInsidePropPanel,
    renderForm,
    onClose,
    updateModel,
    isMessageBarMultiline,
    auxActions,
    display,
  } = props;

  const { theme: formTheme } = display || {};

  const {
    isSubmitting,
    isSaveDisabled,
    errors,
    message,
    onSubmit,
    renderer,
    isFormInitializing,
    model,
    hasFooter,
    tabNavigation,
  } = useForm(props, props?.object, { formId });

  useImperativeHandle(ref, () => ({
    getModel: () => model,
  }));

  useEffect(() => {
    if (updateModel) {
      const hasErrors = !!Object.keys(errors).length;
      updateModel(model, isSaveDisabled || hasErrors);
    }
  }, [errors, isSaveDisabled, model]);

  const styles = useMemo(() => {
    const firstConrol = props?.items?.length === 1 ? (props?.items[0] as IFormField) : undefined;
    const isAloneControlWithEmptyLabel =
      firstConrol?.label && !firstConrol?.hideLabel ? !stringUtils.trim(firstConrol.label) : false;

    let padding: string | number = 0;
    if (!props?.noContentPadding) {
      padding =
        props?.isInsidePropPanel && !props?.noContentPadding
          ? `${
              isAloneControlWithEmptyLabel ? 0 : "8px"
            } ${defaultHorizontalPadding} 20px ${defaultHorizontalPadding}`
          : 0;
    }
    const resultStyles = {
      contentStyle: {
        padding,

        marginTop: isAloneControlWithEmptyLabel ? "-14px" : 0,
        flexGrow: 1,
      },
      titleStyle: {
        width: "100%",
        paddingRight: 60,
        paddingTop: 34,
        paddingLeft: 20,
        position: "relative",
        top: "-20px",
      } as CSSProperties,
      formStyle: { ...props?.style },
    };

    return mergeStyleSets(resultStyles);
  }, [props?.isInsidePropPanel]);

  let footerComp = hasFooter ? (
    <FormFooter
      isOKDisabled={isSaveDisabled}
      errors={errors}
      onClose={() => {
        return signals ? signals.send("close") : onClose && onClose(false);
      }}
      onSubmit={
        footer?.okText !== null
          ? async () => {
              const result = await onSubmit();
              return result;
            }
          : undefined
      }
      saveError={message?.type === "error" ? message?.message : undefined}
      footer={footer}
      style={{
        padding: isInsidePropPanel ? `30px ${defaultHorizontalPadding}` : 0,
      }}
      tabNavigation={tabNavigation}
      formTheme={formTheme}
    />
  ) : (
    <></>
  );

  if (auxActions && auxActions.length > 0) {
    footerComp = (
      <div className={css.relative}>
        {footerComp}

        <Stack
          horizontal
          tokens={cayoTheme.metrics.stackTokens}
          styles={{ root: { position: "absolute", left: 0, bottom: 0 } }}
        >
          {modelUtils.getVisibleItems(auxActions, model).map((a: IButton) => {
            return (
              <DefaultButton
                iconProps={themeUtils.getIconProps(a.icon)}
                // style={themeUtils.getButtonStyle(annotation)}
                styles={getButtonThemeStyles(theme.getButtonThemeStyles(a))}
                key={a.name}
                text={a.text}
                onClick={async () => {
                  if (a.action?.startsWith("model.")) {
                    const { name, value } = ModelExpressionEvaluator.evaluate(model, a.action);
                    setModelProperty(name, value, model);
                  }

                  await onSubmit();
                }}
              />
            );
          })}
        </Stack>
      </div>
    );
  }
  const formContent = (
    <FormWrapper>
      {(isSubmitting || isFormInitializing) && <LoadingOverlay />}
      <FormContentWrapper>
        {message && (
          <MessageBarWrapper
            messageBarType={message?.type === "error" ? "error" : "success"}
            isMultiline={isMessageBarMultiline ?? false}
            truncated
          >
            {message?.message}
          </MessageBarWrapper>
        )}

        <FormContent
          horizontalAlign="stretch"
          overflow={props?.overflow || (props?.isInsidePropPanel ? undefined : "hidden")}
        >
          <Stack tokens={cayoTheme.metrics.stackTokens} className={styles.contentStyle}>
            {!isFormInitializing && renderer()}
          </Stack>
        </FormContent>
      </FormContentWrapper>
      {footerComp}
    </FormWrapper>
  );

  return (
    <>
      {title && (
        <div className={styles.titleStyle}>
          <Text block nowrap={false} variant="large">
            {title}
          </Text>
        </div>
      )}
      <Stack className={`${css.relative} ${css.w100p} ${css.h100p} `}>
        {renderForm === false && <DummyForm>{formContent}</DummyForm>}
        {renderForm !== false && (
          <StyledForm
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                return await onSubmit();
              } catch (e) {
                log.error("Failed to submit form", e);
              }
            }}
            noValidate
            className={`${styles.formStyle} ${className}`}
          >
            {formContent}
          </StyledForm>
        )}
      </Stack>
    </>
  );
});

const MessageBarWrapper = styled(MessageBar)`
  .ms-MessageBar-expandSingleLine {
    .ms-Button {
      width: unset;

      .ms-Button-flexContainer {
        justify-content: center;

        .ms-Stack {
          margin-right: 0px;
          justify-content: flex-end;
        }
      }
    }
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const FormContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: auto;
  height: 100%;
  width: 100%;
`;

const FormContent = styled(Stack)<{ overflow?: string }>`
  height: 100%;
  flex: 1 1 auto;
  flex-flow: column;
  overflow: ${(props) => props.overflow || "hidden auto"};

  div:has(> code) {
    background: #d3d3d3;
  }
`;

const StyledForm = styled.form`
  flex-flow: column;
  inset: 0px;
  display: flex;
  height: 100%;
  position: absolute;

  &.static-form {
    position: static;
  }
`;

const DummyForm = styled.div`
  flex-flow: column;
  inset: 0px;
  display: flex;
  height: 100%;
  width: 100%;
`;

Form.displayName = "Form";
export default Form;
